import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Star, Check, X, Trash2, LogOut, Upload, Loader2, Image as ImageIcon, MessageSquare, Clock, FileText, Phone, Mail, MapPin, BarChart3 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { StatsPanel } from "@/components/StatsPanel";

const BE = process.env.REACT_APP_BACKEND_URL;
const abs = (u) => (u?.startsWith("http") ? u : `${BE}${u}`);
const badge = { pending: "bg-amber-100 text-amber-700", approved: "bg-emerald-100 text-emerald-700", rejected: "bg-red-100 text-red-600" };
const badgeLabel = { pending: "En attente", approved: "Publié", rejected: "Refusé" };

export default function AdminDashboard() {
  const { admin, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("stats");
  const [reviews, setReviews] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [quotes, setQuotes] = useState([]);

  const loadReviews = () => api.get("/admin/reviews").then(({ data }) => setReviews(data)).catch(() => {});
  const loadGallery = () => api.get("/gallery").then(({ data }) => setGallery(data)).catch(() => {});
  const loadQuotes = () => api.get("/admin/quotes").then(({ data }) => setQuotes(data)).catch(() => {});
  useEffect(() => { loadReviews(); loadGallery(); loadQuotes(); }, []);

  const setStatus = async (id, status) => {
    try { await api.patch(`/admin/reviews/${id}`, { status }); loadReviews(); toast.success("Avis mis à jour"); }
    catch { toast.error("Erreur"); }
  };
  const delReview = async (id) => { try { await api.delete(`/admin/reviews/${id}`); loadReviews(); toast.success("Avis supprimé"); } catch { toast.error("Erreur"); } };
  const delGallery = async (id) => { try { await api.delete(`/admin/gallery/${id}`); loadGallery(); toast.success("Réalisation supprimée"); } catch { toast.error("Erreur"); } };
  const delQuote = async (id) => { try { await api.delete(`/admin/quotes/${id}`); loadQuotes(); toast.success("Demande supprimée"); } catch { toast.error("Erreur"); } };

  const pendingCount = reviews.filter((r) => r.status === "pending").length;

  return (
    <div className="min-h-screen bg-sand" data-testid="admin-dashboard">
      <Seo title="Tableau de bord — Sandro Services 06" />
      <header className="border-b border-line bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-5 flex items-center justify-between">
          <div>
            <h1 className="font-display text-2xl text-forest">Administration</h1>
            <p className="text-xs text-ink-soft">{admin?.email}</p>
          </div>
          <button onClick={() => { logout(); nav("/admin/login"); }} data-testid="admin-logout-btn" className="inline-flex items-center gap-2 rounded-full border border-line px-5 py-2.5 text-sm font-semibold text-forest hover:bg-sand-100 transition-all">
            <LogOut size={16} /> Déconnexion
          </button>
        </div>
      </header>

      <div className="max-w-[1200px] mx-auto px-6 py-8">
        <div className="flex gap-2 mb-8 flex-wrap">
          <button onClick={() => setTab("stats")} data-testid="tab-stats" className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-all ${tab === "stats" ? "bg-forest text-sand" : "bg-white border border-line text-ink-soft"}`}>
            <BarChart3 size={16} /> Statistiques
          </button>
          <button onClick={() => setTab("reviews")} data-testid="tab-reviews" className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-all ${tab === "reviews" ? "bg-forest text-sand" : "bg-white border border-line text-ink-soft"}`}>
            <MessageSquare size={16} /> Avis {pendingCount > 0 && <span className="bg-olive text-white text-xs rounded-full px-2 py-0.5">{pendingCount}</span>}
          </button>
          <button onClick={() => setTab("gallery")} data-testid="tab-gallery" className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-all ${tab === "gallery" ? "bg-forest text-sand" : "bg-white border border-line text-ink-soft"}`}>
            <ImageIcon size={16} /> Réalisations
          </button>
          <button onClick={() => setTab("quotes")} data-testid="tab-quotes" className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-all ${tab === "quotes" ? "bg-forest text-sand" : "bg-white border border-line text-ink-soft"}`}>
            <FileText size={16} /> Demandes {quotes.length > 0 && <span className="bg-olive text-white text-xs rounded-full px-2 py-0.5">{quotes.length}</span>}
          </button>
        </div>

        {tab === "stats" && <StatsPanel />}

        {tab === "reviews" && (
          <div className="space-y-4" data-testid="admin-reviews-list">
            {reviews.length === 0 && <p className="text-ink-soft">Aucun avis pour le moment.</p>}
            {reviews.map((r) => (
              <div key={r.id} className="bg-white border border-line rounded-2xl p-6 flex flex-col md:flex-row md:items-center gap-4 justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-semibold text-forest">{r.name}</span>
                    {r.commune && <span className="text-sm text-ink-soft">{r.commune}</span>}
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${badge[r.status]}`}>{badgeLabel[r.status]}</span>
                    <span className="flex">{[1,2,3,4,5].map((n) => <Star key={n} size={14} className={n <= r.rating ? "fill-olive text-olive" : "text-line"} />)}</span>
                  </div>
                  <p className="mt-2 text-ink-soft text-sm">« {r.message} »</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  {r.status !== "approved" && <button onClick={() => setStatus(r.id, "approved")} data-testid={`approve-${r.id}`} className="inline-flex items-center gap-1 rounded-full bg-emerald-600 text-white px-4 py-2 text-sm font-semibold hover:bg-emerald-700 transition-all"><Check size={15} /> Publier</button>}
                  {r.status !== "rejected" && <button onClick={() => setStatus(r.id, "rejected")} className="inline-flex items-center gap-1 rounded-full border border-line px-4 py-2 text-sm font-semibold text-ink-soft hover:bg-sand-100 transition-all"><X size={15} /> Refuser</button>}
                  <button onClick={() => delReview(r.id)} className="inline-flex items-center rounded-full border border-line px-3 py-2 text-red-500 hover:bg-red-50 transition-all"><Trash2 size={15} /></button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "gallery" && (
          <GalleryManager gallery={gallery} reload={loadGallery} onDelete={delGallery} />
        )}

        {tab === "quotes" && (
          <div className="space-y-4" data-testid="admin-quotes-list">
            {quotes.length === 0 && <p className="text-ink-soft">Aucune demande de devis pour le moment.</p>}
            {quotes.map((q) => (
              <div key={q.id} data-testid={`quote-item-${q.id}`} className="bg-white border border-line rounded-2xl p-6">
                <div className="flex flex-col md:flex-row md:items-start gap-4 justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-semibold text-forest text-lg">{q.name}</span>
                      <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-olive/10 text-olive">{q.service_type}</span>
                      <span className="inline-flex items-center gap-1 text-xs text-ink-soft"><Clock size={12} /> {new Date(q.created_at).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" })}</span>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm text-ink-soft">
                      <a href={`tel:${q.phone}`} className="inline-flex items-center gap-1.5 hover:text-olive transition-colors"><Phone size={14} /> {q.phone}</a>
                      <a href={`mailto:${q.email}`} className="inline-flex items-center gap-1.5 hover:text-olive transition-colors"><Mail size={14} /> {q.email}</a>
                      {q.commune && <span className="inline-flex items-center gap-1.5"><MapPin size={14} /> {q.commune}</span>}
                    </div>
                    <p className="mt-3 text-ink text-sm whitespace-pre-wrap bg-sand rounded-xl p-4">{q.description}</p>
                    {q.attachments?.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-3" data-testid={`quote-photos-${q.id}`}>
                        {q.attachments.map((a) => (
                          <a key={a.id} href={abs(a.url)} target="_blank" rel="noreferrer" className="block w-24 h-24 rounded-lg overflow-hidden border border-line hover:ring-2 hover:ring-olive transition-all">
                            <img src={abs(a.url)} alt={a.filename} className="w-full h-full object-cover" />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                  <button onClick={() => delQuote(q.id)} data-testid={`quote-delete-${q.id}`} className="inline-flex items-center rounded-full border border-line px-3 py-2 text-red-500 hover:bg-red-50 transition-all shrink-0"><Trash2 size={15} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const GalleryManager = ({ gallery, reload, onDelete }) => {
  const [form, setForm] = useState({ title: "", description: "", category: "", before_url: "", after_url: "" });
  const [saving, setSaving] = useState(false);
  const beforeRef = useRef(null);
  const afterRef = useRef(null);
  const [uploading, setUploading] = useState("");

  const upload = async (file, field) => {
    setUploading(field);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      setForm((f) => ({ ...f, [field]: data.url }));
      toast.success("Photo téléversée");
    } catch { toast.error("Échec du téléversement"); }
    finally { setUploading(""); }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.title || !form.before_url || !form.after_url) { toast.error("Titre + photo avant + photo après requis."); return; }
    setSaving(true);
    try {
      await api.post("/admin/gallery", form);
      setForm({ title: "", description: "", category: "", before_url: "", after_url: "" });
      if (beforeRef.current) beforeRef.current.value = "";
      if (afterRef.current) afterRef.current.value = "";
      reload();
      toast.success("Réalisation ajoutée");
    } catch { toast.error("Erreur"); }
    finally { setSaving(false); }
  };

  const cls = "w-full rounded-xl border border-line bg-white px-4 py-3 text-ink focus:outline-none focus:ring-2 focus:ring-olive transition-all";
  const Drop = ({ field, refEl, value }) => (
    <div onClick={() => refEl.current?.click()} className="cursor-pointer border-2 border-dashed border-line rounded-xl p-4 text-center hover:border-olive transition-colors relative overflow-hidden aspect-[4/3] grid place-items-center">
      {value ? <img src={`${BE}${value}`} alt={field} className="absolute inset-0 w-full h-full object-cover" /> : (
        uploading === field ? <span className="inline-flex items-center gap-2 text-ink-soft"><Loader2 className="animate-spin" size={16} /> Envoi…</span>
          : <span className="inline-flex items-center gap-2 text-ink-soft text-sm"><Upload size={16} /> {field === "before_url" ? "Photo avant" : "Photo après"}</span>
      )}
      <input ref={refEl} type="file" accept="image/*" hidden data-testid={`gallery-${field}-input`} onChange={(e) => e.target.files[0] && upload(e.target.files[0], field)} />
    </div>
  );

  return (
    <div className="grid lg:grid-cols-[1fr_1.2fr] gap-8" data-testid="admin-gallery">
      <form onSubmit={save} className="bg-white border border-line rounded-2xl p-6 space-y-4 h-fit">
        <h3 className="font-display text-2xl text-forest">Ajouter une réalisation</h3>
        <input data-testid="gallery-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={cls} placeholder="Titre *" />
        <input data-testid="gallery-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={cls} placeholder="Catégorie (ex : Terrasse, Façade)" />
        <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className={cls + " resize-none"} placeholder="Description (facultatif)" />
        <div className="grid grid-cols-2 gap-3">
          <Drop field="before_url" refEl={beforeRef} value={form.before_url} />
          <Drop field="after_url" refEl={afterRef} value={form.after_url} />
        </div>
        <button type="submit" disabled={saving} data-testid="gallery-save" className="w-full inline-flex items-center justify-center gap-2 bg-olive text-white rounded-full px-6 py-3 font-semibold hover:bg-olive-hover transition-all disabled:opacity-60">
          {saving && <Loader2 className="animate-spin" size={16} />} Ajouter
        </button>
      </form>

      <div className="space-y-4">
        {gallery.length === 0 && <p className="text-ink-soft">Aucune réalisation pour le moment.</p>}
        {gallery.map((g) => (
          <div key={g.id} className="bg-white border border-line rounded-2xl p-4 flex gap-4 items-center">
            <div className="flex gap-1 shrink-0">
              <img src={abs(g.before_url)} alt="avant" className="w-20 h-20 object-cover rounded-lg" />
              <img src={abs(g.after_url)} alt="après" className="w-20 h-20 object-cover rounded-lg" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-forest">{g.title}</p>
              {g.category && <p className="text-xs text-olive">{g.category}</p>}
              {g.description && <p className="text-sm text-ink-soft">{g.description}</p>}
            </div>
            <button onClick={() => onDelete(g.id)} data-testid={`gallery-delete-${g.id}`} className="inline-flex items-center rounded-full border border-line px-3 py-2 text-red-500 hover:bg-red-50 transition-all"><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
    </div>
  );
};
